<template lang="pug">
form(@submit.prevent='submit')
  .c-media.c-comments__media
    .c-media__left
      .c-comments__avatar
        .c-crop.c-crop--circle
          img.c-crop__img(
            :src="dummyUserIcon",
            :key="webp(user.avatar_image)",
            v-lazy="{src: webp(user.avatar_image), error: dummyUserIcon, loading: dummyUserIcon}",
            :class='cropModifierClass',
            :alt='user.name'
          )
    .c-media__body.c-comments__body
      .c-comments__editor
        textarea.form-ctl.c-comments__form(:placeholder='placeholder', :rows='rows', v-text='value', v-model='comment', :class="{ 'dirty': dirty }", @focus='focus', @blur='blur', @keydown='keydown', :readonly='!isLogin')
        vue-emoji-picker.c-comments__emoji-picker(:input-node='elemTextarea && elemTextarea[0]', :disabled='!isLogin')
      .c-comments__buttons(v-if="isLogin")
        p.u-align-left.u-color-muted.u-fs-xs.u-mbxs(v-if="isLogin") This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank">Terms of Service</a> apply.
        button.c-btn.c-btn--secondary.u-mrs(v-if='buttons.cancel', @click.prevent='cancel')
          | {{buttons.cancel.value}}
        button.c-btn.c-btn--primary.c-btn--icon(v-if='buttons.submit', type='submit', :disabled='isEmpty || !canPost')
          i(:class='labelIconClass', v-show='labelIconClass')
          | {{buttons.submit.value}}
      .c-comments__buttons(v-else)
        a.c-btn.c-btn--primary.c-btn--icon(v-if='buttons.login', type='button', :href='loginUrl', style='opacity: 0.5;', target="_parent")
          span {{buttons.login.value}}
</template>

<script>
const TEXTAREA_ROWS = 3;
const TEXTAREA_LINE_HEIGHT = 20;
const TEXTAREA_PADDING_VERTICAL = 15;

import { default as urlUtil } from '../../util/url';
import { userCommentsStore } from '../../stores/UserComments';
import emojiPicker from '../emojiPicker/emojiPicker.vue';
import { I18n } from '../../util/mixin/i18n.mixin';

const FORM_TYPES = {
  POST: 'post',
  REPLY: 'reply',
  EDIT: 'edit',
  REPORT: 'report'
};

export default {
  mixins: [I18n],
  components: {
    'vue-emoji-picker': emojiPicker
  },
  props: {
    type: String,
    rows: {
      type: Number,
      default: TEXTAREA_ROWS
    },
    value: String,
    placeholder: String,
    buttons: {
      type: Object,
      default() {
        return {
          submit: {
            value: 'Submit'
          },
          cancel: {
            value: 'Cancel'
          },
          login: {
            value: 'Log in to Post'
          }
        };
      }
    }
  },

  data() {
    return {
      comment: '',
      // 1度でも focus されたら true に
      dirty: false,
      elemTextarea: null
    };
  },

  computed: {
    user() {
      return userCommentsStore.getUserInfo();
    },
    thread() {
      return userCommentsStore.getThreadInfo();
    },
    isLogin() {
      return userCommentsStore.isUserLogin();
    },
    canPost() {
      return userCommentsStore.canPost();
    },

    isEmpty() {
      return this.comment.trim() === '';
    },

    labelIconClass() {
      switch (this.type) {
        case FORM_TYPES.REPORT:
          return 'icon-flag';
        case FORM_TYPES.EDIT:
          return 'icon-comment';
        case FORM_TYPES.REPLY:
          return 'icon-comment-alt';
        case FORM_TYPES.POST:
          return 'icon-comment';
        default:
          return '';
      }
    },

    loginUrl() {
      return `/login?r=${window.parent.location.pathname}${window.parent.location.search}&via=comment`;
    },

    cropModifierClass() {
      if (this.user.avatar_image_width > this.user.avatar_image_height) {
        return 'c-crop__img--h';
      }
      return '';
    },

    dummyUserIcon() {
      return urlUtil.cloudImageUrl('/static/common/noAvatarImage100.png');
    }
  },

  created() {
    this.$root.$on('UPDATED', () => {
      this.comment = this.value || '';
      if (this.dirty) {
        this.fixTextareaHeight();
        this.dirty = false;
      }
    });
  },

  inject: ['requestRecaptcha'],

  methods: {
    webp: urlUtil.webp,

    focus(e) {
      if (!e) {
        // メソッド経由でtextareaにfocusする場合のみ
        this.elemTextarea.trigger('focus');
      } else {
        // 絵文字ピッカーで絵文字を選択しただけではmodel（@comment）が
        // アップデートされない問題のワークアラウンド
        this.comment = e.target.value;
        this.dirty = true;

        // focusで高さが変化するので親に伝える
        if (window.vueEventBus && window.vueEventBus.userComments) {
          window.vueEventBus.userComments.$emit('CHANGE_HEIGHT');
        }
      }
    },

    blur() {
      // blurで高さが変化するので親に伝える
      if (window.vueEventBus && window.vueEventBus.userComments) {
        window.vueEventBus.userComments.$emit('CHANGE_HEIGHT');
      }
    },

    cancel() {
      return this.$emit('cancel');
    },

    async submit() {
      if (this.isEmpty || !this.canPost) {
        return;
      }
      // v-model.trimだとselectionStartが正しく取得できず絵文字が先頭にくるケースがあるためsubmit時に整形
      this.comment = this.comment.trim();

      this.elemTextarea.trigger('blur');

      let recaptchaToken;
      try {
        recaptchaToken = await this.requestRecaptcha();
      } catch (err) {
        alert('An error occurred. Please wait a moment before trying again.');
        return;
      }
      this.$emit('submit', { 'g-recaptcha-response': recaptchaToken });
    },

    keydown(e) {
      // Ctrl/⌘ + enter
      if ((e.ctrlKey || e.metaKey) && e.keyCode === 13) {
        this.submit();
      }
    },

    fixTextareaHeight() {
      if (this.comment) {
        const nl = this.comment.match(/\n|\r\n/g) || [];
        const row =
          nl === null ? TEXTAREA_ROWS : Math.max(nl.length + 1, TEXTAREA_ROWS);
        this.elemTextarea.height(
          row * TEXTAREA_LINE_HEIGHT + TEXTAREA_PADDING_VERTICAL
        );
      } else {
        this.elemTextarea.height('');
      }
      // 改行で高さが変化するので親に伝える
      if (window.vueEventBus && window.vueEventBus.userComments) {
        window.vueEventBus.userComments.$emit('CHANGE_HEIGHT');
      }
    }
  },

  mounted() {
    this.elemTextarea = $(this.$el).find('textarea');
    this.elemTextarea.on('input propertychange', () => {
      this.fixTextareaHeight();
    });
  }
};
</script>
